import React, { useState } from 'react';
import { ScenarioObjective } from '../types';

interface ObjectiveEditorProps {
  objectives: ScenarioObjective[];
  guidelines: string[];
  onChange: (objectives: ScenarioObjective[]) => void;
  onGuidelinesChange: (guidelines: string[]) => void;
}

export const ObjectiveEditor: React.FC<ObjectiveEditorProps> = ({
  objectives,
  guidelines,
  onChange,
  onGuidelinesChange,
}) => {
  const [newObjTitle, setNewObjTitle] = useState('');
  const [newGuideline, setNewGuideline] = useState('');

  const handleAddObjective = () => {
    if (!newObjTitle.trim()) return;
    const newObj: ScenarioObjective = {
      id: `obj_${Date.now()}`,
      title: newObjTitle.trim(),
      required: true,
    };
    onChange([...objectives, newObj]);
    setNewObjTitle('');
  };

  const handleRemoveObjective = (index: number) => {
    const updated = [...objectives];
    updated.splice(index, 1);
    onChange(updated);
  };

  const handleToggleRequired = (index: number) => {
    const updated = [...objectives];
    updated[index] = { ...updated[index], required: !updated[index].required };
    onChange(updated);
  };

  const handleAddGuideline = () => {
    if (!newGuideline.trim()) return;
    onGuidelinesChange([...guidelines, newGuideline.trim()]);
    setNewGuideline('');
  };

  const handleRemoveGuideline = (index: number) => {
    const updated = [...guidelines];
    updated.splice(index, 1);
    onGuidelinesChange(updated);
  };

  return (
    <div style={{ display: 'grid', gap: '24px' }}>
      {/* Simulation Key Objectives */}
      <div>
        <div style={{ marginBottom: '12px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>Learner Performance Objectives</h4>
          <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '2px 0 0' }}>Key conversational goals the learner must satisfy to pass.</p>
        </div>

        <div style={{ display: 'grid', gap: '8px', marginBottom: '12px' }}>
          {objectives.length === 0 ? (
            <div style={{ padding: '16px', borderRadius: '8px', border: '1px dashed var(--line)', textAlign: 'center', fontSize: '12.5px', color: 'var(--muted)', background: 'var(--surface)' }}>
              No specific objectives configured. Add core goals below.
            </div>
          ) : (
            objectives.map((obj, idx) => (
              <div
                key={obj.id || idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '10px',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  background: 'var(--surface)',
                  border: '1px solid var(--line)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1 1 200px', minWidth: 0, paddingRight: '8px' }}>
                  <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'var(--blue-soft)', color: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11.5px', fontWeight: 700, flexShrink: 0 }}>
                    {idx + 1}
                  </span>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--ink)', wordBreak: 'break-word' }}>{obj.title}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => handleToggleRequired(idx)}
                    className={`admin-status-badge ${obj.required ? 'admin-status-badge--active' : 'admin-status-badge--pending'}`}
                    style={{ cursor: 'pointer', border: 'none' }}
                  >
                    {obj.required ? 'Mandatory' : 'Optional'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveObjective(idx)}
                    className="admin-btn admin-btn--danger admin-btn--sm"
                    style={{ padding: '4px 8px' }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input
            type="text"
            className="admin-form__input"
            placeholder="Add learner objective (e.g. Verify account owner details before sharing balance)..."
            value={newObjTitle}
            onChange={(e) => setNewObjTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddObjective();
              }
            }}
            style={{ flex: '1 1 200px', minWidth: 0 }}
          />
          <button
            type="button"
            onClick={handleAddObjective}
            className="admin-btn admin-btn--secondary"
            style={{ flexShrink: 0 }}
          >
            + Add Goal
          </button>
        </div>
      </div>

      {/* Required Behaviors & Protocol Guidelines */}
      <div>
        <div style={{ marginBottom: '12px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>Required Protocol Behaviors</h4>
          <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '2px 0 0' }}>Specific operational steps (greeting, empathy statements, escalation paths).</p>
        </div>

        <div style={{ display: 'grid', gap: '8px', marginBottom: '12px' }}>
          {guidelines.length === 0 ? (
            <div style={{ padding: '16px', borderRadius: '8px', border: '1px dashed var(--line)', textAlign: 'center', fontSize: '12.5px', color: 'var(--muted)', background: 'var(--surface)' }}>
              No protocol guidelines configured. Add step behaviors below.
            </div>
          ) : (
            guidelines.map((g, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '10px',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  background: 'var(--surface)',
                  border: '1px solid var(--line)',
                }}
              >
                <span style={{ fontSize: '13px', color: 'var(--ink)', flex: '1 1 200px', minWidth: 0, wordBreak: 'break-word' }}>{g}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveGuideline(idx)}
                  className="admin-btn admin-btn--danger admin-btn--sm"
                  style={{ padding: '4px 8px', flexShrink: 0 }}
                >
                  ✕
                </button>
              </div>
            ))
          )}
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input
            type="text"
            className="admin-form__input"
            placeholder="Add behavior guideline (e.g. Demonstrate active listening before proposing solution)..."
            value={newGuideline}
            onChange={(e) => setNewGuideline(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddGuideline();
              }
            }}
            style={{ flex: '1 1 200px', minWidth: 0 }}
          />
          <button
            type="button"
            onClick={handleAddGuideline}
            className="admin-btn admin-btn--secondary"
            style={{ flexShrink: 0 }}
          >
            + Add Behavior
          </button>
        </div>
      </div>
    </div>
  );
};
